import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import {
  BuildingCreateRequestDTO,
  BuildingDeleteRequestDTO,
  BuildingDTO,
  BuildingList,
  BuildingMasterCreateBulkRequestDTO,
  BuildingReadByIdRequestDTO,
  BuildingReadBySocietyIdRequestDTO,
  BuildingUpdateRequestDTO,
} from './building.interface';

import {
  Building_Create,
  Building_Update,
  Building_Delete,
  Building_ReadByUserId,
  Building_ReadAll,
  Building_ReadById,
  Building_ReadBySocietyId,
  Building_ReadBySocietyId_External,
  Building_CreateBulk,
} from 'src/app/GlobalVariable';
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class BuildingService {
  http = inject(HttpClient);

  constructor() {}

  Building_Create(
    data: BuildingCreateRequestDTO
  ): Observable<BuildingDTO> {
    return this.http.post<BuildingDTO>(
      Building_Create,
      data
    );
  }

  Building_Update(
    data: BuildingUpdateRequestDTO
  ): Observable<BuildingDTO> {
    return this.http.post<BuildingDTO>(
      Building_Update,
      data
    );
  }

  Building_Delete(
    data: BuildingDeleteRequestDTO
  ): Observable<void> {
    return this.http.post<void>(
      Building_Delete,
      data
    );
  }

  Building_ReadAll(): Observable<BuildingList> {
    return this.http.get<BuildingList>(
      Building_ReadAll
    );
  }

  Building_ReadByUserId(
    data: BuildingReadByIdRequestDTO
  ): Observable<BuildingDTO> {
    return this.http.post<BuildingDTO>(
      Building_ReadByUserId,
      data
    );
  }

  Building_ReadById(
    data: BuildingReadByIdRequestDTO
  ): Observable<BuildingDTO> {
    return this.http.post<BuildingDTO>(
      Building_ReadById,
      data
    );
  }

  Building_ReadBySocietyId(
    data: BuildingReadBySocietyIdRequestDTO
  ): Observable<BuildingList> {
    /*
     * Support both societyId and SocietyId so an incorrect
     * property-case from any existing component does not
     * become an empty encrypted request.
     */
    const receivedSocietyId = Number(
      data?.societyId ??
      (data as any)?.SocietyId ??
      0
    );

    /*
     * Prevent calling the backend with:
     * {}
     * { societyId: 0 }
     * { societyId: null }
     * { societyId: undefined }
     * { societyId: NaN }
     */
    if (
      !Number.isFinite(receivedSocietyId) ||
      receivedSocietyId <= 0
    ) {
      console.error(
        'Building_ReadBySocietyId stopped because societyId is invalid:',
        data
      );

      return throwError(
        () =>
          new Error(
            'Valid societyId is required for Building_ReadBySocietyId'
          )
      );
    }

    /*
     * Preserve any existing DTO fields while always sending
     * the required societyId using the correct property name.
     *
     * Your existing interceptor can continue encrypting this
     * normal payload. Do not manually encrypt it here.
     */
    const payload: BuildingReadBySocietyIdRequestDTO = {
      ...data,
      societyId: receivedSocietyId,
    };

    return this.http.post<BuildingList>(
      Building_ReadBySocietyId,
      payload
    );
  }

  Building_ReadBySocietyId_External(
    data: BuildingReadBySocietyIdRequestDTO,
    xApiKey?: string,
    xApiTimestamp?: string,
    xApiNonce?: string,
    xApiSignature?: string
  ): Observable<BuildingList> {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (xApiKey && xApiSignature) {
      headers = headers
        .set('X-Api-Key', xApiKey)
        .set('X-Api-Timestamp', xApiTimestamp || '')
        .set('X-Api-Nonce', xApiNonce || '')
        .set('X-Api-Signature', xApiSignature);
    }
    return this.http.post<BuildingList>(
      Building_ReadBySocietyId_External,
      data,
      { headers }
    );
  }

  Building_CreateBulk(
    data: BuildingMasterCreateBulkRequestDTO
  ): Observable<BuildingList> {
    return this.http.post<BuildingList>(
      Building_CreateBulk,
      data
    );
  }
}